import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Reply, Star } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { nomeDaRede } from "./redes";
import Cabecalho from "./Cabecalho";

// Avaliações do Facebook e do Google Meu Negócio, respondidas daqui.
//
// A resposta é pública e substitui a anterior — não é uma conversa. Por isso
// a que já existe aparece no lugar do campo, em vez de somar mais uma.

const Estrelas = ({ nota }) => (
  <span className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`h-3.5 w-3.5 ${
          n <= (nota || 0)
            ? "fill-amber-400 text-amber-400"
            : "text-muted-foreground/40"
        }`}
      />
    ))}
  </span>
);

const Avaliacoes = () => {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [respostas, setRespostas] = useState({});
  const [enviando, setEnviando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/zernio/reviews");
      setAvaliacoes(data.data || []);
      setResumo(data.summary || null);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const responder = async (avaliacao) => {
    const texto = (respostas[avaliacao.id] || "").trim();

    if (!texto) {
      toast.error("Escreva a resposta.");
      return;
    }

    setEnviando(avaliacao.id);

    try {
      await api.post(`/zernio/reviews/${avaliacao.id}/reply`, {
        accountId: avaliacao.accountId,
        message: texto
      });

      toast.success("Resposta publicada.");
      setRespostas((atuais) => ({ ...atuais, [avaliacao.id]: "" }));
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setEnviando(null);
  };

  if (carregando) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <Cabecalho
        titulo="Avaliações"
        descricao="O que os clientes avaliaram no Facebook e no Google Meu Negócio. A resposta é pública e substitui a anterior."
      />

      {resumo?.totalReviews > 0 && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3 text-sm">
          <Estrelas nota={Math.round(resumo.averageRating || 0)} />
          <span className="font-medium">
            {Number(resumo.averageRating || 0).toFixed(1)}
          </span>
          <span className="text-muted-foreground">
            {resumo.totalReviews} avaliações
          </span>
        </div>
      )}

      {avaliacoes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhuma avaliação nas contas conectadas. Só Facebook e Google Meu
          Negócio têm avaliação.
        </p>
      ) : (
        avaliacoes.map((avaliacao) => (
          <div key={avaliacao.id} className="rounded-lg border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Estrelas nota={avaliacao.rating} />
              <span className="text-sm font-medium">
                {avaliacao.reviewerName || "Anônimo"}
              </span>
              <Badge variant="secondary">
                {nomeDaRede(avaliacao.platform)}
              </Badge>
            </div>

            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm">
              {avaliacao.text || "(sem comentário)"}
            </p>

            {avaliacao.reply?.text ? (
              <div className="mt-2 rounded-md border-l-2 border-primary bg-muted/40 p-2 text-sm">
                <p className="text-xs font-medium text-muted-foreground">
                  Sua resposta
                </p>
                <p className="mt-0.5 whitespace-pre-wrap">
                  {avaliacao.reply.text}
                </p>
              </div>
            ) : (
              <>
                <Textarea
                  rows={2}
                  className="mt-2"
                  placeholder="Responder publicamente…"
                  value={respostas[avaliacao.id] || ""}
                  onChange={(e) =>
                    setRespostas((atuais) => ({
                      ...atuais,
                      [avaliacao.id]: e.target.value
                    }))
                  }
                />

                <Button
                  size="sm"
                  className="mt-2"
                  onClick={() => responder(avaliacao)}
                  disabled={enviando !== null}
                >
                  {enviando === avaliacao.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Reply className="h-4 w-4" />
                  )}
                  Responder
                </Button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default Avaliacoes;
